import { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Button,
    CircularProgress,
    Dialog,
    IconButton
} from "@mui/material";
import {
    CheckCircle as CheckIcon,
    Cancel as XIcon,
    Description as FileIcon,
    Close as CloseIcon,
    ArrowBackIos as PrevIcon,
    ArrowForwardIos as NextIcon
} from "@mui/icons-material";
import { getLatestMeasurements } from "../../services/BodyMeasurementsService";
import { getFormById } from "../../services/FormTemplateService";
import { imageUrl } from "../../utils/imageUrl";

const FONT = '"Heebo", "Roboto", sans-serif';

// ─── BODY WEIGHT ────────────────────────────────────────────
export const BodyWeightSection = ({ item }) => {
    if (!item.completed) {
        return <PendingPlaceholder />;
    }

    const unit = item.weightUnit === "LBS" ? "lbs" : "ק״ג";
    const change = (item.weightValue != null && item.previousWeightValue != null)
        ? (item.weightValue - item.previousWeightValue).toFixed(1)
        : null;

    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 3, py: 1 }}>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                <Typography sx={{ fontSize: "36px", fontWeight: 700, color: "#1E293B", fontFamily: FONT }}>
                    {item.weightValue ?? "—"}
                </Typography>
                <Typography sx={{ fontSize: "18px", fontWeight: 500, color: "#64748B", fontFamily: FONT }}>
                    {unit}
                </Typography>
            </Box>
            {item.previousWeightValue != null && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    <Typography sx={{ fontSize: "13px", color: "#94A3B8", fontFamily: FONT }}>
                        משקל קודם: {item.previousWeightValue} {unit}
                    </Typography>
                    {change != null && (
                        <Typography sx={{
                            fontSize: "13px",
                            fontWeight: 600,
                            fontFamily: FONT,
                            color: parseFloat(change) === 0 ? "#64748B"
                                : parseFloat(change) < 0 ? "#10B981"
                                : "#EF4444"
                        }}>
                            {parseFloat(change) > 0 ? `+${change}` : change} {unit}
                        </Typography>
                    )}
                </Box>
            )}
        </Box>
    );
};

// ─── BODY MEASUREMENTS ──────────────────────────────────────
export const MeasurementsSection = ({ traineeId, item }) => {
    const [measurements, setMeasurements] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (item.completed && traineeId) {
            setLoading(true);
            getLatestMeasurements(traineeId)
                .then(res => setMeasurements(res.data?.measurements || res.data || []))
                .catch(() => setMeasurements([]))
                .finally(() => setLoading(false));
        }
    }, [traineeId, item.completed]);

    if (!item.completed) {
        return <PendingPlaceholder />;
    }

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={24} sx={{ color: "#57837E" }} />
            </Box>
        );
    }

    if (!measurements || measurements.length === 0) {
        return (
            <Typography sx={{ fontSize: "14px", color: "#94A3B8", py: 1, fontFamily: FONT }}>
                אין נתוני מדידות
            </Typography>
        );
    }

    const getMeasurementLabel = (m) =>
        m.definitionNameHeb || m.definitionName || '';

    return (
        <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #E2E8F0", borderRadius: "8px" }}>
            <Table size="small">
                <TableHead>
                    <TableRow sx={{ backgroundColor: "#F8FAFC" }}>
                        <TableCell sx={{ fontWeight: 600, fontSize: "12px", color: "#64748B", fontFamily: FONT }}>
                            סוג מדידה
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: "12px", color: "#64748B", fontFamily: FONT }}>
                            ערך נוכחי
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: "12px", color: "#64748B", fontFamily: FONT }}>
                            ערך קודם
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: "12px", color: "#64748B", fontFamily: FONT }}>
                            שינוי
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {measurements.map((m, idx) => {
                        const change = (m.value != null && m.previousValue != null)
                            ? (m.value - m.previousValue).toFixed(1)
                            : null;
                        return (
                            <TableRow key={idx} sx={{ "&:last-child td": { borderBottom: 0 } }}>
                                <TableCell sx={{ fontSize: "13px", color: "#1E293B", fontFamily: FONT }}>
                                    {getMeasurementLabel(m)}
                                </TableCell>
                                <TableCell sx={{ fontSize: "13px", color: "#1E293B", fontWeight: 600, fontFamily: FONT }}>
                                    {m.value ?? "—"}
                                </TableCell>
                                <TableCell sx={{ fontSize: "13px", color: "#64748B", fontFamily: FONT }}>
                                    {m.previousValue ?? "—"}
                                </TableCell>
                                <TableCell sx={{
                                    fontSize: "13px",
                                    fontWeight: 600,
                                    fontFamily: FONT,
                                    color: change == null ? "#64748B"
                                        : parseFloat(change) < 0 ? "#10B981"
                                        : parseFloat(change) > 0 ? "#EF4444"
                                        : "#64748B"
                                }}>
                                    {change == null ? "—" : (parseFloat(change) > 0 ? `+${change}` : change)}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

// ─── PROGRESS PHOTOS ────────────────────────────────────────
export const PhotosSection = ({ item }) => {
    const [lightboxIndex, setLightboxIndex] = useState(-1);

    if (!item.completed) {
        return <PendingPlaceholder />;
    }

    const photos = (item.photoUrls || []).map((url, idx) => ({ id: idx, photoUrl: url }));

    if (photos.length === 0) {
        return (
            <Typography sx={{ fontSize: "14px", color: "#94A3B8", py: 1, fontFamily: FONT }}>
                אין תמונות
            </Typography>
        );
    }

    return (
        <>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                {photos.map((photo, idx) => (
                    <Box
                        key={photo.id || idx}
                        onClick={() => setLightboxIndex(idx)}
                        sx={{
                            width: { xs: 150, md: 200 },
                            borderRadius: "8px",
                            overflow: "hidden",
                            cursor: "pointer",
                            border: "1px solid #E2E8F0",
                            transition: "transform 0.15s",
                            "&:hover": { transform: "scale(1.02)" }
                        }}
                    >
                        <Box
                            component="img"
                            src={imageUrl(photo.photoUrl, 200)}
                            alt=""
                            sx={{ width: "100%", display: "block" }}
                        />
                    </Box>
                ))}
            </Box>

            {/* Lightbox */}
            <Dialog
                open={lightboxIndex >= 0}
                onClose={() => setLightboxIndex(-1)}
                maxWidth="md"
                PaperProps={{ sx: { borderRadius: "12px", backgroundColor: "#000", overflow: "hidden" } }}
            >
                {lightboxIndex >= 0 && photos[lightboxIndex] && (
                    <Box sx={{ position: "relative" }}>
                        <IconButton
                            onClick={() => setLightboxIndex(-1)}
                            sx={{ position: "absolute", top: 8, right: 8, color: "#fff", zIndex: 1 }}
                        >
                            <CloseIcon />
                        </IconButton>
                        {photos.length > 1 && (
                            <>
                                <IconButton
                                    onClick={() => setLightboxIndex((lightboxIndex - 1 + photos.length) % photos.length)}
                                    sx={{ position: "absolute", top: "50%", left: 8, transform: "translateY(-50%)", color: "#fff", zIndex: 1 }}
                                >
                                    <PrevIcon />
                                </IconButton>
                                <IconButton
                                    onClick={() => setLightboxIndex((lightboxIndex + 1) % photos.length)}
                                    sx={{ position: "absolute", top: "50%", right: 8, transform: "translateY(-50%)", color: "#fff", zIndex: 1 }}
                                >
                                    <NextIcon />
                                </IconButton>
                            </>
                        )}
                        <Box
                            component="img"
                            src={photos[lightboxIndex].photoUrl}
                            alt=""
                            sx={{ maxWidth: "90vw", maxHeight: "85vh", display: "block" }}
                        />
                    </Box>
                )}
            </Dialog>
        </>
    );
};

// ─── FORM Q&A ───────────────────────────────────────────────
export const FormSection = ({ item }) => {
    const [formData, setFormData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (item.completed && item.linkedEntityId) {
            setLoading(true);
            getFormById(item.linkedEntityId)
                .then(res => setFormData(res.data))
                .catch(() => setFormData(null))
                .finally(() => setLoading(false));
        }
    }, [item.completed, item.linkedEntityId]);

    if (!item.completed) {
        return <PendingPlaceholder />;
    }

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={24} sx={{ color: "#57837E" }} />
            </Box>
        );
    }

    if (!formData) {
        return (
            <Typography sx={{ fontSize: "14px", color: "#94A3B8", py: 1, fontFamily: FONT }}>
                לא ניתן לטעון את הטופס
            </Typography>
        );
    }

    const questions = formData.questions || [];
    const answers = formData.answers || [];

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {questions.map((question, index) => {
                const answer = answers.find(a => a.questionId === question.id);

                // Display-only types
                if (["FORM_TITLE", "SECTION_TITLE", "FREE_TEXT", "LINK", "PDF_ATTACHMENT"].includes(question.questionType)) {
                    if (question.questionType === "FORM_TITLE") {
                        return (
                            <Typography key={index} sx={{ fontSize: "18px", fontWeight: 600, color: "#1E293B", textAlign: "center", fontFamily: FONT }}>
                                {question.title}
                            </Typography>
                        );
                    }
                    if (question.questionType === "SECTION_TITLE") {
                        return (
                            <Typography key={index} sx={{ fontSize: "15px", fontWeight: 600, color: "#1E293B", borderBottom: "2px solid #57837E", paddingBottom: "6px", fontFamily: FONT }}>
                                {question.title}
                            </Typography>
                        );
                    }
                    return null;
                }

                return (
                    <Box key={index}>
                        <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#1E293B", marginBottom: "6px", fontFamily: FONT }}>
                            {question.question}
                        </Typography>

                        {/* TEXT types */}
                        {["SHORT_TEXT", "LONG_TEXT", "EMAIL", "PHONE"].includes(question.questionType) && (
                            <Typography sx={{ fontSize: "14px", color: "#64748B", paddingRight: "16px", fontFamily: FONT }}>
                                {answer?.text || "—"}
                            </Typography>
                        )}

                        {/* YES_NO */}
                        {question.questionType === "YES_NO" && (
                            <Box sx={{ paddingRight: "16px" }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    {answer?.answer === true ? (
                                        <>
                                            <CheckIcon sx={{ color: "#10B981", fontSize: "20px" }} />
                                            <Typography sx={{ fontSize: "14px", color: "#64748B", fontFamily: FONT }}>כן</Typography>
                                        </>
                                    ) : (
                                        <>
                                            <XIcon sx={{ color: "#EF4444", fontSize: "20px" }} />
                                            <Typography sx={{ fontSize: "14px", color: "#64748B", fontFamily: FONT }}>לא</Typography>
                                        </>
                                    )}
                                </Box>
                                {answer?.answer === true && answer?.details && (
                                    <Typography sx={{ fontSize: "13px", color: "#64748B", mt: 1, paddingRight: "28px", fontFamily: FONT }}>
                                        <Typography component="span" sx={{ color: "#94A3B8" }}>פרטים נוספים: </Typography>
                                        {answer.details}
                                    </Typography>
                                )}
                                {answer?.answer === false && answer?.detailsNo && (
                                    <Typography sx={{ fontSize: "13px", color: "#64748B", mt: 1, paddingRight: "28px", fontFamily: FONT }}>
                                        <Typography component="span" sx={{ color: "#94A3B8" }}>פרטים נוספים: </Typography>
                                        {answer.detailsNo}
                                    </Typography>
                                )}
                            </Box>
                        )}

                        {/* MULTIPLE_CHOICE */}
                        {question.questionType === "MULTIPLE_CHOICE" && (
                            <Box sx={{ paddingRight: "16px", display: "flex", flexWrap: "wrap", gap: 1 }}>
                                {(answer?.selectedOptions || []).map((option, idx) => (
                                    <Chip key={idx} label={option} size="small" sx={{ backgroundColor: "rgba(87, 131, 126, 0.1)", color: "#57837E", fontSize: "13px" }} />
                                ))}
                                {(!answer?.selectedOptions || answer.selectedOptions.length === 0) && (
                                    <Typography sx={{ fontSize: "14px", color: "#94A3B8", fontFamily: FONT }}>—</Typography>
                                )}
                            </Box>
                        )}

                        {/* NUMBER */}
                        {question.questionType === "NUMBER" && (
                            <Typography sx={{ fontSize: "14px", color: "#64748B", paddingRight: "16px", fontFamily: FONT }}>
                                {answer?.number ?? "—"}
                            </Typography>
                        )}

                        {/* DATE */}
                        {question.questionType === "DATE" && (
                            <Typography sx={{ fontSize: "14px", color: "#64748B", paddingRight: "16px", fontFamily: FONT }}>
                                {Array.isArray(answer?.date) ? `${answer.date[2]}/${answer.date[1]}/${answer.date[0]}` : (answer?.date || "—")}
                            </Typography>
                        )}

                        {/* RATING */}
                        {question.questionType === "RATING" && (
                            <Typography sx={{ fontSize: "14px", color: "#64748B", paddingRight: "16px", fontFamily: FONT }}>
                                {answer?.rating || 0} / {question.maxRating || 5}
                            </Typography>
                        )}

                        {/* CHECKBOX */}
                        {question.questionType === "CHECKBOX" && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, paddingRight: "16px" }}>
                                {answer?.checked ? (
                                    <><CheckIcon sx={{ color: "#10B981", fontSize: "20px" }} /><Typography sx={{ fontSize: "14px", color: "#64748B", fontFamily: FONT }}>מאושר</Typography></>
                                ) : (
                                    <><XIcon sx={{ color: "#EF4444", fontSize: "20px" }} /><Typography sx={{ fontSize: "14px", color: "#64748B", fontFamily: FONT }}>לא מאושר</Typography></>
                                )}
                            </Box>
                        )}

                        {/* FILE_UPLOAD */}
                        {question.questionType === "FILE_UPLOAD" && answer?.uploadedFileUrls?.length > 0 && (
                            <Box sx={{ paddingRight: "16px" }}>
                                <Button
                                    variant="text"
                                    href={answer.uploadedFileUrls[0]}
                                    target="_blank"
                                    startIcon={<FileIcon sx={{ marginLeft: "8px" }} />}
                                    sx={{ color: "#57837E", fontSize: "14px", textDecoration: "underline", fontFamily: FONT }}
                                >
                                    צפה בקובץ
                                </Button>
                            </Box>
                        )}

                        {/* SIGNATURE */}
                        {question.questionType === "SIGNATURE" && (() => {
                            const signatureUrl = answer?.signatureImageUrl
                                || answers.find(a => a.answerType === "SIGNATURE")?.signatureImageUrl;
                            return signatureUrl ? (
                                <Box sx={{ paddingRight: "16px" }}>
                                    <Box
                                        sx={{
                                            border: "2px solid #E2E8F0",
                                            borderRadius: "8px",
                                            padding: "12px",
                                            backgroundColor: "#FFFFFF",
                                            maxWidth: "320px"
                                        }}
                                    >
                                        <img
                                            src={signatureUrl}
                                            alt="חתימה דיגיטלית"
                                            crossOrigin="anonymous"
                                            style={{
                                                width: "100%",
                                                height: "120px",
                                                objectFit: "contain",
                                                backgroundColor: "#FFFFFF"
                                            }}
                                        />
                                    </Box>
                                </Box>
                            ) : (
                                <Typography sx={{ fontSize: "14px", color: "#94A3B8", paddingRight: "16px", fontFamily: FONT }}>
                                    —
                                </Typography>
                            );
                        })()}
                    </Box>
                );
            })}
        </Box>
    );
};

// ─── PENDING PLACEHOLDER ────────────────────────────────────
const PendingPlaceholder = () => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
        <Typography sx={{ fontSize: "14px", color: "#94A3B8", fontFamily: FONT }}>
            ממתין למילוי
        </Typography>
    </Box>
);
